import type { ECharts } from "echarts";
import type { GeoScatterBoundary } from "@/shared/charts/components/geo-scatter-chart/types";

/** Fast but smooth: short enough to feel snappy, long enough to read as a zoom, not a jump. */
const FOCUS_ANIMATION_DURATION_MS = 450;
/** Leaves a margin around the focused area instead of touching the viewport edges. */
const FOCUS_PADDING_RATIO = 0.85;
/**
 * Bounds how many frames to wait for the geo coordinate system to exist
 * (e.g. right after mount, while the chart's initial, deferred `setOption`
 * is still pending) before giving up on this focus pass.
 */
const MAX_READY_ATTEMPTS = 30;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function getBoundingBox(boundaries: GeoScatterBoundary[]) {
  const points = boundaries.flatMap((boundary) => boundary.points);
  if (points.length === 0) return null;

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/**
 * `convertToPixel`/`convertFromPixel` return `undefined` (or throw) until the
 * geo coordinate system exists, so both are treated as "not ready yet"
 * rather than an error.
 */
function toPixel(chart: ECharts, point: [number, number]): [number, number] | null {
  try {
    return (chart.convertToPixel({ geoIndex: 0 }, point) as [number, number] | undefined) ?? null;
  } catch {
    return null;
  }
}

function fromPixel(chart: ECharts, point: [number, number]): [number, number] | null {
  try {
    return (chart.convertFromPixel({ geoIndex: 0 }, point) as [number, number] | undefined) ?? null;
  } catch {
    return null;
  }
}

/**
 * Smoothly zooms/pans the geo layer so every given boundary is visible,
 * covering as much of the viewport as `zoomLimit` allows. Drives the geo
 * component's `center`/`zoom` directly via `setOption` (merged, so it
 * doesn't disturb series data) since ECharts has no built-in animated
 * "fit to bounds" for geo. Returns a cleanup function that cancels the
 * in-flight animation (or the wait for readiness), e.g. if the focused
 * boundaries change again.
 */
export function focusGeoOnBoundaries(
  chart: ECharts,
  container: HTMLElement,
  boundaries: GeoScatterBoundary[],
  zoomLimit: { min: number; max: number }
): () => void {
  const box = getBoundingBox(boundaries);
  if (!box) return () => {};
  const bounds = box;

  let frameId: number;
  let attempts = 0;

  function start() {
    const fromPixelMin = toPixel(chart, [bounds.minX, bounds.minY]);
    const fromPixelMax = toPixel(chart, [bounds.maxX, bounds.maxY]);
    if (!fromPixelMin || !fromPixelMax) {
      if (attempts++ < MAX_READY_ATTEMPTS) frameId = requestAnimationFrame(start);
      return;
    }

    const targetCenter: [number, number] = [
      (bounds.minX + bounds.maxX) / 2,
      (bounds.minY + bounds.maxY) / 2,
    ];
    const fromCenter =
      fromPixel(chart, [container.clientWidth / 2, container.clientHeight / 2]) ?? targetCenter;
    const boundaryPixelWidth = Math.abs(fromPixelMax[0] - fromPixelMin[0]) || 1;
    const boundaryPixelHeight = Math.abs(fromPixelMax[1] - fromPixelMin[1]) || 1;
    const scaleFactor = Math.min(
      (container.clientWidth * FOCUS_PADDING_RATIO) / boundaryPixelWidth,
      (container.clientHeight * FOCUS_PADDING_RATIO) / boundaryPixelHeight
    );

    const geoOption = (chart.getOption().geo as Array<{ zoom?: number }> | undefined)?.[0];
    const fromZoom = geoOption?.zoom ?? 1;
    const targetZoom = Math.min(Math.max(fromZoom * scaleFactor, zoomLimit.min), zoomLimit.max);

    const startTime = performance.now();

    function step(now: number) {
      const t = Math.min((now - startTime) / FOCUS_ANIMATION_DURATION_MS, 1);
      const eased = easeOutCubic(t);
      chart.setOption(
        {
          geo: {
            center: [
              fromCenter[0] + (targetCenter[0] - fromCenter[0]) * eased,
              fromCenter[1] + (targetCenter[1] - fromCenter[1]) * eased,
            ],
            zoom: fromZoom + (targetZoom - fromZoom) * eased,
          },
        },
        { lazyUpdate: true }
      );
      if (t < 1) frameId = requestAnimationFrame(step);
    }
    frameId = requestAnimationFrame(step);
  }

  frameId = requestAnimationFrame(start);
  return () => cancelAnimationFrame(frameId);
}
